import { keepPreviousData, useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { endpoints, type DocumentEnvelope, type Item, type ItemListResponse } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * "My List" — the saved jobs a bill line is built from (§11.1, §7).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS LIST IS FOR. A fabricator bills the same dozen jobs over and over:
 * shutter repair, trolley wheel, welding at site. Each one carries an HSN/SAC
 * code and a GST rate that §4.1 requires on every invoice line and that nobody
 * remembers. Saving the job once means the next bill is two taps instead of
 * typing a code the owner would otherwise guess.
 *
 * `active` DEFAULTS TO TRUE ON THE SERVER, not here. `ItemListQuerySchema`
 * declares `.default(true)` — "a deactivated job should never surface in the
 * picker" — so sending nothing shows only live jobs. This screen sends the
 * parameter EXPLICITLY when the owner asks for retired ones, and omits it
 * otherwise rather than sending `true` and duplicating a default that could
 * drift.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const ITEM_PAGE_SIZE = 50;

export interface ItemFilters {
  /** Matched against name, description AND the HSN code by the server. */
  q?: string;
  /** `undefined` uses the server's default (live only); `false` shows retired. */
  active?: boolean;
  offset: number;
}

export function useItemFilters(): {
  filters: ItemFilters;
  setFilters: (patch: Partial<ItemFilters>) => void;
  clearFilters: () => void;
  activeCount: number;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<ItemFilters>(() => {
    const rawActive = params.get("active");
    const rawOffset = params.get("offset");
    const q = params.get("q") ?? undefined;

    return {
      q: q === "" ? undefined : q,
      active: rawActive === null || rawActive === "" ? undefined : rawActive === "true",
      offset: rawOffset === null ? 0 : Math.max(0, Number(rawOffset) || 0),
    };
  }, [params]);

  const setFilters = useCallback(
    (patch: Partial<ItemFilters>) => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      }
      if (!("offset" in patch)) next.delete("offset");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  const activeCount = [filters.q, filters.active].filter((value) => value !== undefined).length;

  return { filters, setFilters, clearFilters, activeCount };
}

export function useItemList(filters: ItemFilters): UseQueryResult<ItemListResponse> {
  const query = {
    q: filters.q,
    active: filters.active,
    limit: ITEM_PAGE_SIZE,
    offset: filters.offset === 0 ? undefined : filters.offset,
  };

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();

  return useQuery({
    queryKey: queryKeys.items.list(query),
    queryFn: () => api.get<ItemListResponse>(`${endpoints.items}${qs === "" ? "" : `?${qs}`}`),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

/** `GET /items/:id` → `{ data }`. */
export function useItem(id: string | undefined): UseQueryResult<Item> {
  return useQuery({
    queryKey: queryKeys.items.detail(id ?? ""),
    queryFn: async () => {
      const response = await api.get<DocumentEnvelope<Item>>(endpoints.item(id ?? ""));
      return response.data;
    },
    enabled: id !== undefined && id !== "",
    staleTime: 60_000,
  });
}

/**
 * Create or update.
 *
 * INVALIDATES EVERY ITEM QUERY, because the bill editor's picker reads the same
 * list. Saving a new rate that the picker then kept serving from cache would put
 * the OLD rate on the next bill — the one place where a stale cache becomes a
 * wrong number on a legal document rather than a cosmetic lag.
 */
export function useSaveItem(id: string | undefined) {
  const queryClient = useQueryClient();
  const isEdit = id !== undefined && id !== "";

  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const response = isEdit
        ? await api.put<DocumentEnvelope<Item>>(endpoints.item(id), body)
        : await api.post<DocumentEnvelope<Item>>(endpoints.items, body);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.items.all });
    },
  });
}

/**
 * The HSN/SAC codes the CA has approved (§8.6).
 *
 * A SMALL, STATIC, CA-VERIFIED SEED — never a lookup API. §8.6 rules that out in
 * as many words: the rate is not a property of the code, SAC 9954 spans 1%–18%
 * depending on the building, the immovable-property relationship and who the
 * customer is. An API returning a flat answer would put an unverifiable rate on
 * a legal tax invoice.
 */
export function useHsnSearch(q: string) {
  const trimmed = q.trim();
  const search = new URLSearchParams({ limit: "20" });
  if (trimmed !== "") search.set("q", trimmed);

  return useQuery({
    queryKey: queryKeys.hsn.search(trimmed),
    queryFn: () => api.get<HsnListResponse>(`${endpoints.hsn}?${search.toString()}`),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
}

/**
 * One rate period of one code (§8.7).
 *
 * THE RATE FIELD IS `defaultGstRateBp`, NOT `gstRateBp`. "Default" is doing real
 * work in that name: it is what this code USUALLY attracts, and §8.6 is explicit
 * that the rate is not a property of the code — SAC 9954 spans 1% to 18%
 * depending on the building and who the customer is. The line keeps its own
 * `gstRateBp`, snapshotted at save time, and that is the one an invoice is
 * taxed at.
 */
export interface HsnCode {
  id: number;
  code: string;
  description: string;
  defaultGstRateBp: number;
  supplyNature: string;
  isService: boolean;
  /** Inclusive start of this rate period; `supersededAt` null means current. */
  effectiveFrom: string;
  supersededAt: string | null;
  /** §8.6 — when the CA last confirmed this rate, and who. Null is a warning. */
  lastVerifiedOn: string | null;
  lastVerifiedBy: string | null;
}

/** `GET /hsn` — the envelope key is `codes`, and `asOf` is the date they resolve at. */
export interface HsnListResponse {
  codes: HsnCode[];
  asOf: string;
}
